import { DateInputLocalized } from '@components'

import {
  Button,
  ButtonsGroup,
  FormItemsGroup,
  Input,
  Modal,
  ModalProps,
  notify,
  Spacing,
  useModal,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBankAccount } from '@features/bankAccounts/hooks/useBankAccount'
import {
  UpdateConnectedBankAccountPayload,
  useUpdateConnectedBankAccount,
} from '@features/bankAccounts/hooks/useUpdateConnectedBankAccount'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useForm } from '../../../../../../hooks'
import {
  EditConnectedBankAccountForm,
  EditConnectedBankAccountFormSchema,
  getDefaultValues,
  getValidationSchema,
} from './utils/formData'

interface EditConnectedBankAccountModalProps extends ModalProps {
  bankAccountId: string
}

export const EditConnectedBankAccountModal = ({
  id,
  bankAccountId,
}: EditConnectedBankAccountModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close: closeModal } = useModal(id)
  const { organization } = useUserOrganization()
  const { bankAccount } = useBankAccount(bankAccountId)
  const { Form, FormItem, handleSubmit } = useForm<EditConnectedBankAccountFormSchema>({
    defaultValues: useMemo(() => getDefaultValues(bankAccount), [bankAccount]),
    validationSchema: useMemo(() => getValidationSchema(t), [t]),
  })

  const { update, isProcessing } = useUpdateConnectedBankAccount({
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.BankAccounts)
      closeModal()
      notify({
        id: NotificationKeys.BankAccountCustomNameEdit,
        message: t('bank.account.modal.success.edit'),
        variant: 'success',
      })
    },
    onError: () => {
      notify({
        id: NotificationKeys.BankAccountCustomNameEdit,
        message: t('bank.account.modal.error.edit'),
        variant: 'error',
      })
    },
  })

  const submitForm = useCallback(() => {
    if (!organization) {
      return
    }

    handleSubmit((data: EditConnectedBankAccountForm) => {
      const payload: UpdateConnectedBankAccountPayload = {
        organizationId: organization?.id,
        bankAccountId,
        customAccountName: data.customAccountName || null,
        reconciliationStartDate: data.reconciliationStartDate || null,
      }
      update(payload)
    })()
  }, [organization, bankAccountId, handleSubmit, update])

  return (
    <Modal id={id} size="s">
      <Modal.Header title={t('bank.account.modal.header.edit')} />
      <Modal.Body>
        <Form>
          <FormItemsGroup rowSpacing={Spacing.S16}>
            <FormItem
              name="customAccountName"
              label={t('bank.account.modal.account_custom_name')}
              render={({ field: { value, onChange, ...rest } }) => (
                <Input
                  {...rest}
                  placeholder={t('bank.account.modal.account_custom_name.placeholder')}
                  value={value}
                  onChange={onChange}
                />
              )}
            />
            <FormItem
              name="reconciliationStartDate"
              label={t('bank.account.modal.reconciliation_start_date')}
              render={({ field }) => <DateInputLocalized allowClear {...field} />}
            />
          </FormItemsGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <ButtonsGroup>
          <Button onClick={closeModal} variant="text">
            {t('cancel')}
          </Button>
          <Button loading={isProcessing} onClick={submitForm}>
            {t('save')}
          </Button>
        </ButtonsGroup>
      </Modal.Footer>
    </Modal>
  )
}
